/* eslint-disable camelcase, no-console */
import join from 'lodash/fp/join';
import { ErrorResponse, User, UserManager, UserManagerSettings, UserProfile as Profile } from 'oidc-client-ts';

import { config, isTraining, isWorkshopParentFrontend } from '@/config';
import { OAuthConfig } from '@/configuration';
import { AccessToken } from '@/configuration/tokenHandling/tokenSlice';
import { UserProfile } from './loginSlice';
import { mapUserProfile } from './userProfile';

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;

const isErrorResponse = (error: unknown): error is ErrorResponse => error instanceof ErrorResponse;

const retrySigninSilent = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.signinSilent().catch((error: Error) => {
        if (isErrorResponse(error) && error.error === 'login_required') {
            oauthConfig.onSessionExpired();
        } else {
            setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
        }
    });
};

export interface SessionRenewedResult {
    accessToken: AccessToken;
    idToken: Profile;
    profile: UserProfile;
    locale: string;
}

const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
    accessToken: result.access_token,
    idToken: result.profile,
    locale: result.profile?.locale ?? 'en-GB',
    profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
    const redirectUri = config.login.redirectUri;
    const silentRedirectUri = config.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', config.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        staleStateAgeInSeconds: 600,
        extraQueryParams: { isServiceCareTraining: isTraining() ? 'true' : 'false' },
    };

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.events.addUserLoaded((user) => {
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user as User));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(() => {
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut((...args) => {
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

// Mock constants (also check LocalJwtTokenService.kt on backend)
const MOCK_CLIENT_ID = '11111111-1111-1111-1111-111111111111';
const MOCK_USER_FIRST_NAME = 'John';
const MOCK_USER_LAST_NAME = 'Doe';
const MOCK_USER_FULL_NAME = `${MOCK_USER_FIRST_NAME} ${MOCK_USER_LAST_NAME}`;
const MOCK_USER_EMAIL = 'john.doe@digitalhub.man';
const MOCK_SUB = '22222222-2222-2222-2222-222222222222';

// --- MAN ---
const MOCK_ISSUER_MAN = 'http://localhost-sso:1111';
const MOCK_USER_ID = 'A0001';

// --- RIO ---
const MOCK_ISSUER_RIO = 'Issuer Identifier';
const MOCK_TEST_SUBJECT_ID_READ_WRITE_PERMISSION = 'prod-rio-users:mock-user';
const MOCK_READ_SCOPE = 'openid profile email';
const MOCK_TEST_ACCOUNT_ID = 'mockaccount';

export const base64UrlEncode = (obj: Record<string, unknown>): string =>
    btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export const createMockJwt = (payload: Record<string, unknown>): string => {
    const header = base64UrlEncode({ alg: 'RS256', typ: 'JWT' });
    const body = base64UrlEncode(payload);
    return `${header}.${body}.mock-signature`;
};

// Token creation (also check LocalJwtTokenService.kt on backend)
const createWorkshopAccessToken = (): string => {
    const now = Math.floor(Date.now() / 1000);
    return createMockJwt({
        iss: MOCK_ISSUER_MAN,
        exp: now + 86400,
        nbf: now - 1,
        aud: [MOCK_CLIENT_ID],
        MFAPolicy: 'false',
        tid: crypto.randomUUID(),
        given_name: MOCK_USER_FIRST_NAME,
        family_name: MOCK_USER_LAST_NAME,
        name: MOCK_USER_FULL_NAME,
        idp: 'MANAADDEV',
        extension_Internal: 'X',
        MANId: MOCK_USER_ID,
        extension_UserEmail: MOCK_USER_EMAIL,
        Organisationstyp: 'W',
        city: 'Muenchen',
        department: 'DEPARTMENT001',
        CompanyInfo: 'MAN Truck & Bus SE',
        streetAddress: 'Dachauer Straße 667',
        postalCode: '80995',
        postalCity: 'München',
        OrgName: 'W00001',
        manCostCenter: 'G2525',
        isServiceCareTraining: isTraining() ? 'true' : 'false',
        salutation: 'Mr.',
        sub: MOCK_SUB,
        userAccess: true,
        azp: MOCK_CLIENT_ID,
        ver: '1.0',
        iat: now - 3600,
    });
};

const createWorkshopIdToken = (): Record<string, unknown> => {
    const now = Math.floor(Date.now() / 1000);
    return {
        exp: now + 86400,
        nbf: now - 1,
        ver: '1.0',
        iss: MOCK_ISSUER_MAN,
        sub: MOCK_SUB,
        aud: [MOCK_CLIENT_ID],
        acr: 'something-relevant',
        iat: now - 3600,
        auth_time: now + 86400,
        MFAPolicy: 'false',
        tid: crypto.randomUUID(),
        given_name: MOCK_USER_FIRST_NAME,
        family_name: MOCK_USER_LAST_NAME,
        name: MOCK_USER_FULL_NAME,
        idp: 'MANAADDEV',
        extension_Internal: 'X',
        MANId: MOCK_USER_ID,
        extension_UserEmail: MOCK_USER_EMAIL,
        Organisationstyp: 'W',
        city: 'Muenchen',
        department: 'DEPARTMENT001',
        CompanyInfo: 'MAN Truck & Bus SE',
        streetAddress: 'Dachauer Straße 667',
        postalCode: '80995',
        postalCity: 'München',
        OrgName: 'W00001',
        manCostCenter: 'G2525',
        isServiceCareTraining: 'true',
        salutation: 'Mr.',
        userAccess: true,
        azp: MOCK_CLIENT_ID,
        locale: config.login.mockLocale,
    };
};

const createRioAccessToken = (): string => {
    const now = Math.floor(Date.now() / 1000);
    return createMockJwt({
        iss: MOCK_ISSUER_RIO,
        exp: now + 86400,
        aud: MOCK_CLIENT_ID,
        sub: `prod-rio-users:${MOCK_SUB}`,
        scope: MOCK_READ_SCOPE,
        account: MOCK_TEST_ACCOUNT_ID,
        azp: MOCK_CLIENT_ID,
        email: MOCK_USER_EMAIL,
        given_name: MOCK_USER_FIRST_NAME,
        family_name: MOCK_USER_LAST_NAME,
        name: MOCK_USER_FULL_NAME,
    });
};

const createRioIdToken = (): Record<string, unknown> => ({
    iss: MOCK_ISSUER_RIO,
    aud: MOCK_CLIENT_ID,
    exp: Math.floor(Date.now() / 1000) + 86400,
    iat: Math.floor(Date.now() / 1000) - 3600,
    sub: `prod-rio-users:${MOCK_SUB}`,
    account: MOCK_TEST_ACCOUNT_ID,
    azp: MOCK_CLIENT_ID,
    email: MOCK_USER_EMAIL,
    given_name: MOCK_USER_FIRST_NAME,
    family_name: MOCK_USER_LAST_NAME,
    name: MOCK_USER_FULL_NAME,
    locale: config.login.mockLocale,
});

export const configureMockUserManager = (oauthConfig: OAuthConfig): UserManager => {
    console.warn('[configuration/login/oidc-session] Using mocked authorization due to config setting');

    const isWorkshop = isWorkshopParentFrontend();
    const accessToken = isWorkshop ? createWorkshopAccessToken() : createRioAccessToken();
    const profile = isWorkshop ? createWorkshopIdToken() : createRioIdToken();
    const idToken = createMockJwt(profile);

    const signinSilent = () => {
        const userSettings = {
            access_token: accessToken,
            profile: {
                ...profile,
                locale: config.login.mockLocale,
            } as Profile,
            id_token: idToken,
            session_state: 'session_state',
            refresh_token: 'refresh_token',
            token_type: 'Bearer',
            scope: 'openid profile email',
            expires_at: Math.floor(Date.now() / 1000) + 86400,
            state: 'state',
        };

        const user = new User(userSettings);
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
        return Promise.resolve(user);
    };

    const signinRedirect = () => {
        console.warn('[configuration/login/oidc-session] signinRedirect called on mock — returning silently');
        return signinSilent().then(() => {});
    };

    const clearStaleState = () => {
        console.info('[configuration/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    return {
        signinSilent,
        signinRedirect,
        clearStaleState,
        events: {
            addUserLoaded: () => {},
            addUserUnloaded: () => {},
            addSilentRenewError: () => {},
            addUserSignedOut: () => {},
        },
    } as unknown as UserManager;
};
